import {lazy, Suspense} from 'react';
import {createBrowserRouter} from 'react-router-dom';
import MainLayout from '@layouts/MainLayout';
import EpisodeLayout from '@layouts/EpisodeLayout';
import ProtocolLayout from '@layouts/ProtocolLayout';
import GlobalLogicLayout from '@layouts/GlobalLogicLayout';
import WorkspaceLayout from '@layouts/WorkspaceLayout';
import DesktopLayout from '@layouts/DesktopLayout';
import ErrorBoundary from '@components/common/ErrorBoundary';
import AuthRoute from '@components/common/AuthRoute';

// 采用懒加载以切割出独立的打包 Chunk
const InfiniteCanvasPage = lazy(() => import('@pages/canvas'));
const HomePage = lazy(() => import('@pages/home'));
const ProjectAssetsPage = lazy(() => import('@pages/assets'));
const MyAssetsPage = lazy(() => import('@pages/myAssets'));
const ScriptPage = lazy(() => import('@pages/script'));
const WorkspacePage = lazy(() => import('@/pages/workspace'));
const EpisodePage = lazy(() => import('@pages/episode'));
const AgentEpisodePage = lazy(() => import('@pages/agent/episode'));
const PlatformAgreementPage = lazy(() => import('@pages/platformAgreement'));
const PrivacyPolicyPage = lazy(() => import('@pages/privacyPolicy'));
const MembershipAgreementPage = lazy(() => import('@pages/membershipAgreement'));
const CutDocsPage = lazy(() => import('@pages/cutDocs'));

// 简单的全屏 Loading，或者使用你自定义的 Spinner 组件
const LazyWrapper = ({children}: {children: React.ReactNode}) => {
    return (
        <ErrorBoundary level="page">
            <Suspense fallback={null}>{children}</Suspense>
        </ErrorBoundary>
    );
};

const router = createBrowserRouter([
    {
        element: <GlobalLogicLayout />,
        children: [
            {
                path: '/',
                element: <MainLayout />,
                children: [
                    {
                        index: true,
                        element: (
                            <LazyWrapper>
                                <HomePage />
                            </LazyWrapper>
                        ),
                    },
                    {
                        path: 'asset',
                        element: (
                            <AuthRoute>
                                <LazyWrapper>
                                    <MyAssetsPage />
                                </LazyWrapper>
                            </AuthRoute>
                        ),
                    },
                ],
            },
            {
                element: <ProtocolLayout />,
                children: [
                    {
                        path: 'platformAgreement',
                        element: (
                            <LazyWrapper>
                                <PlatformAgreementPage />
                            </LazyWrapper>
                        ),
                    },
                    {
                        path: 'privacyPolicy',
                        element: (
                            <LazyWrapper>
                                <PrivacyPolicyPage />
                            </LazyWrapper>
                        ),
                    },
                    {
                        path: 'membershipAgreement',
                        element: (
                            <LazyWrapper>
                                <MembershipAgreementPage />
                            </LazyWrapper>
                        ),
                    },
                    {
                        path: 'cut-docs',
                        element: (
                            <LazyWrapper>
                                <CutDocsPage />
                            </LazyWrapper>
                        ),
                    },
                ],
            },
            {
                path: 'workspace',
                element: <WorkspaceLayout />,
                children: [
                    {
                        index: true,
                        element: (
                            <AuthRoute requireProjectId>
                                <LazyWrapper>
                                    <WorkspacePage />
                                </LazyWrapper>
                            </AuthRoute>
                        ),
                    },
                ],
            },
            {
                path: 'workspace/assets',
                element: <DesktopLayout />,
                children: [
                    {
                        index: true,
                        element: (
                            <AuthRoute requireProjectId>
                                <LazyWrapper>
                                    <ProjectAssetsPage />
                                </LazyWrapper>
                            </AuthRoute>
                        ),
                    },
                ],
            },
            {
                path: 'canvas',
                element: (
                    <AuthRoute requireProjectId>
                        <LazyWrapper>
                            <InfiniteCanvasPage />
                        </LazyWrapper>
                    </AuthRoute>
                ),
            },
            {
                path: 'script',
                element: <DesktopLayout />,
                children: [
                    {
                        index: true,
                        element: (
                            <AuthRoute>
                                <LazyWrapper>
                                    <ScriptPage />
                                </LazyWrapper>
                            </AuthRoute>
                        ),
                    },
                ],
            },
            {
                path: '/project/episode',
                element: <EpisodeLayout />,
                children: [
                    {
                        index: true,
                        element: (
                            <AuthRoute requireProjectId>
                                <LazyWrapper>
                                    <EpisodePage />
                                </LazyWrapper>
                            </AuthRoute>
                        ),
                    },
                ],
            },
            {
                path: '/agent/episode',
                element: (
                    <AuthRoute requireProjectId>
                        <LazyWrapper>
                            <AgentEpisodePage />
                        </LazyWrapper>
                    </AuthRoute>
                ),
            },
        ],
    },
]);

export default router;
